// src/components/HomePage.jsx
import React, { useEffect } from 'react';
import {
    FaBirthdayCake,
    FaBolt,
    FaBriefcase,
    FaCheckCircle,
    FaCloudUploadAlt,
    FaHeadphones,
    FaHeart,
    FaInfinity,
    FaLock,
    FaMapMarkedAlt,
    FaMobileAlt,
    FaPalette,
    FaPhotoVideo,
    FaPrayingHands,
    FaQrcode,
    FaRegSmile,
    FaSnowflake,
    FaUsers,
} from 'react-icons/fa';
import { MdOutlineCollectionsBookmark } from 'react-icons/md';
import { DEFAULT_BILLING_CONFIG, getBillingPricing } from '../utils/billing';

export default function HomePage({ navigate, onGetStarted, routeParams = {}, billingConfig = DEFAULT_BILLING_CONFIG }) {
    const pricing = getBillingPricing(billingConfig);
    const offerPreview = getBillingPricing(billingConfig, pricing.offerCode);
    const priceLabel = pricing.finalPriceLabel;
    const hasActiveOffer = pricing.offerActive;
    const hasDiscountedPrice = pricing.discountCents > 0;

    useEffect(() => {
        document.title = 'GatherLens | Capture Every Moment with a Simple QR Code';
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute(
                'content',
                'GatherLens lets your guests capture and share every photo, video, audio message, and note from your wedding or event — all through a simple QR code, no app required. Create a private gallery for weddings, parties, and celebrations with unlimited full-quality uploads.'
            );
        }
    }, []);

    useEffect(() => {
        const section = routeParams?.route;
        if (section && ['pricing', 'faq', 'occasions'].includes(section)) {
            const targetId = section === 'pricing' ? 'pricing' : section === 'faq' ? 'faq' : section;
            const scrollToTarget = () => {
                const el = document.getElementById(targetId);
                if (el) {
                    const top = el.getBoundingClientRect().top + window.scrollY - 100;
                    window.scrollTo({ top, behavior: 'smooth' });
                }
            };
            const timeoutId = window.setTimeout(scrollToTarget, 200);
            return () => window.clearTimeout(timeoutId);
        }
        return undefined;
    }, [routeParams]);

    const highlightCards = [
        {
            icon: <FaPhotoVideo className="text-3xl text-[var(--gold)]" />,
            title: 'Photo & video sharing without limits',
            description:
                'Guests upload in full quality from any device—no app, no accounts, and unlimited albums, guests, and submissions.',
        },
        {
            icon: <FaHeadphones className="text-3xl text-[var(--gold)]" />,
            title: 'Heartfelt messages & video guestbook',
            description:
                'Invite guests to leave voice notes, toasts, and written messages that turn into keepsake cards alongside your gallery.',
        },
        {
            icon: <FaQrcode className="text-3xl text-[var(--gold)]" />,
            title: 'Elegant QR signage ready to print',
            description:
                'Personalized upload pages, printable QR templates, and optional 4-digit access PINs keep sharing grandma-and-grandpa friendly.',
        },
        {
            icon: <FaCloudUploadAlt className="text-3xl text-[var(--gold)]" />,
            title: 'Live slideshow & cloud reliability',
            description:
                'Celebrate in real time while GatherLens keeps every file safe for 12 months with easy download-all zip folders.',
        },
    ];

    const whyGatherLens = [
        {
            icon: <FaHeart className="text-3xl text-[var(--gold)]" />,
            title: 'Cherish every sentiment',
            copy: 'Pair photos, videos, and audio notes in one gallery designed to feel like a keepsake from the first upload.',
        },
        {
            icon: <FaLock className="text-3xl text-[var(--gold)]" />,
            title: 'You are always the curator',
            copy: 'Host controls stay gentle and intuitive—remove any upload with a tap while keeping everything else flowing.',
        },
        {
            icon: <FaRegSmile className="text-3xl text-[var(--gold)]" />,
            title: 'Effortless for every guest',
            copy: 'Guests follow a simple "Scan A QR Code > Upload Photos and Videos > Done" path that works on any device and never asks them to create an account.',
        },
    ];

    const guestJourney = [
        {
            title: 'Capture the moment',
            description: 'Guests snap photos, videos, or audio on their phones—no special gear required.',
            icon: <FaRegSmile className="text-2xl text-[var(--gold)]" />,
        },
        {
            title: 'Scan your QR card',
            description: 'Our printable signage and digital invites share one private link secured by an optional four-digit PIN.',
            icon: <FaQrcode className="text-2xl text-[var(--gold)]" />,
        },
        {
            title: 'Tap upload media',
            description: 'Guests choose photos, videos, or voice notes in seconds—no app downloads or logins.',
            icon: <FaMobileAlt className="text-2xl text-[var(--gold)]" />,
        },
        {
            title: 'Celebrate instantly',
            description: 'Uploads appear in your host dashboard and live slideshow, and you can gently remove anything that does not fit the story. Audio and message buttons are right along side upload.',
            icon: <FaBolt className="text-2xl text-[var(--gold)]" />,
        },
    ];

    const hostToolkit = [
        {
            heading: 'Make it yours',
            icon: <FaPalette className="text-xl text-[var(--gold)]" />,
            bullets: [
                'Custom cover photo, welcome message, and color palette',
                'Event hashtag, album imagery, and curated album order',
                'Co-owners can collaborate on content and private albums',
            ],
        },
        {
            heading: 'Total peace of mind',
            icon: <FaLock className="text-xl text-[var(--gold)]" />,
            bullets: [
                'Optional 4-digit PIN for guests',
                'Private albums and a simple delete-anytime control keep every memory just how you want it',
                'Privacy-first platform—your content is never repurposed or compressed',
            ],
        },
        {
            heading: 'Built for the whole celebration',
            icon: <FaUsers className="text-xl text-[var(--gold)]" />,
            bullets: [
                'Unlimited guests, albums, and upload volume at one price',
                'Multi-device compatibility for phones, tablets, and desktop hosts',
                'Download everything per album or grab the entire event in organized zip folders',
            ],
        },
    ];

    const eventExperienceSections = [
        {
            slug: 'weddings',
            title: 'Weddings',
            icon: <FaHeart className="text-3xl text-[var(--gold)]" />,
            summary:
                'Curate a private gallery for the entire wedding weekend with co-host controls and polished signage that fits your palette.',
            details: [
                'Share one QR code across the welcome party, ceremony, reception, and farewell brunch so every candid lands in one timeline.',
                'Collect voice toasts, photo booth strips, and candid clips without asking guests to download an app or create accounts.',
                'Download full-quality albums for thank-you notes, anniversary slideshows, and heirloom photo books in minutes.',
            ],
            cta: 'Explore #weddings ideas',
        },
        {
            slug: 'birthday-party',
            title: 'Milestone Birthdays',
            icon: <FaBirthdayCake className="text-3xl text-[var(--gold)]" />,
            summary:
                'Give sweet sixteens, 30th bashes, and 80th tributes a shared hub where surprises, speeches, and highlights live together.',
            details: [
                'Encourage guests to drop throwback photos, video tributes, and heartfelt notes in organized albums for every part of the party.',
                'Display a moderated slideshow during cake cutting or toasts so the celebration keeps buzzing in real time.',
                'Send one recap link the next morning with download-all folders ready for reels, scrapbooks, and thank-you messages.',
            ],
            cta: 'Plan #birthday-party magic',
        },
        {
            slug: 'corporate-event',
            title: 'Product Launches & Corporate Retreats',
            icon: <FaBriefcase className="text-3xl text-[var(--gold)]" />,
            summary:
                'Deliver leadership, marketing, and client teams a polished media library that reflects your brand voice.',
            details: [
                'Brand the upload page with campaign colors, sponsor shout-outs, and talking points guests see before sharing.',
                'Capture behind-the-scenes clips, keynote reactions, and VIP testimonials in neatly labeled albums ready for follow-up.',
                'Export curated media packs and highlight reels for press kits, recap emails, and social media stories instantly.',
            ],
            cta: 'See #corporate-event playbooks',
        },
        {
            slug: 'celebration-of-life',
            title: 'Celebrations of Life & Funerals',
            icon: <FaPrayingHands className="text-3xl text-[var(--gold)]" />,
            summary:
                'Offer families a thoughtful, private space to gather comforting tributes before, during, and after the service.',
            details: [
                'Invite relatives near and far to upload photos, stories, and audio memories the moment they feel ready.',
                'Use gentle moderation tools to ensure every submission feels respectful while staying simple for grieving guests.',
                'Download tribute albums to share with loved ones or display during memorial gatherings without worrying about file loss.',
            ],
            cta: 'Visit #celebration-of-life support',
        },
        {
            slug: 'holiday-party',
            title: 'Holiday Gatherings',
            icon: <FaSnowflake className="text-3xl text-[var(--gold)]" />,
            summary:
                'Capture traditions from Friendsgiving feasts to sparkling New Year’s Eve countdowns in a single festive hub.',
            details: [
                'Print QR signage for dessert tables, cocoa bars, or office party check-ins so sharing stays effortless.',
                'Keep a moderated slideshow running to replay new uploads while everyone mingles or makes a toast.',
                'Reuse the gallery for annual recap emails, family letters, or company newsletters packed with genuine highlights.',
            ],
            cta: 'Browse #holiday-party tips',
        },
        {
            slug: 'family-reunion',
            title: 'Family Reunions & Group Travel',
            icon: <FaMapMarkedAlt className="text-3xl text-[var(--gold)]" />,
            summary:
                'Keep the group chat calm and the shared itinerary organized, whether you are reuniting across generations or jet-setting together.',
            details: [
                'Organize albums by day, activity, or family branch so every highlight is easy to revisit.',
                'Encourage grandparents and grandkids alike with simple QR scanning and no-login uploads from any device.',
                'Save the entire archive for future planning, printed keepsakes, or a digital time capsule everyone can treasure.',
            ],
            cta: 'Discover #family-reunion ideas',
        },
    ];

    const faqs = [
        {
            q: 'Do guests need to download an app?',
            a: 'Never. Guests scan your QR code or tap the private link, enter the PIN you chose, and upload instantly from their browser.',
        },
        {
            q: 'How private is our content?',
            a: 'You decide what stays—remove any upload in a moment, tuck memories into private albums, or pause guest access whenever needed. GatherLens never reuses your media.',
        },
        {
            q: 'Can I test before paying?',
            a: 'Yes. Your private workspace includes unlimited albums and owner uploads. Activate the Event Pass only when you’re ready to reveal the guest URL and QR code.',
        },
    ];

    return (
        <div className="bg-[var(--off-white)]">
            {/* Hero */}
            <section id="home" className="hero-bg relative min-h-[82vh] overflow-hidden flex items-center">
                <div className="hero-orb hero-orb--one" aria-hidden="true" />
                <div className="hero-orb hero-orb--two" aria-hidden="true" />
                <div className="container relative z-10 mx-auto px-4 sm:px-6 py-24 text-center text-white">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-5 py-2 text-xs font-semibold uppercase tracking-[0.22em] backdrop-blur">
                        <FaInfinity className="text-[var(--gold)]" />
                        Build free · {priceLabel} when ready to share
                    </div>
                    <h1 className="mx-auto mt-8 max-w-5xl text-5xl sm:text-6xl lg:text-8xl font-bold font-serif leading-[0.98] tracking-[-0.035em]">
                        Every guest photo. One beautiful place.
                    </h1>
                    <p className="mt-7 text-base sm:text-lg lg:text-xl text-white/75 max-w-2xl mx-auto leading-relaxed">
                        Create the gallery, upload without limits, and perfect every detail for free. Pay once only when you’re ready to reveal the private guest link and QR code.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
                        <button
                            onClick={onGetStarted}
                            className="btn-gold w-full sm:w-auto py-4 px-10 text-lg shadow-lg transform hover:scale-105"
                        >
                            Create Your Event — Free
                        </button>
                        <button
                            onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                            className="w-full sm:w-auto rounded-lg border border-white/80 py-4 px-8 text-lg font-semibold text-white transition hover:bg-white/10"
                        >
                            See How It Works
                        </button>
                    </div>
                    <div className="mx-auto mt-10 flex max-w-2xl flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/65">
                        <span>No app for guests</span><span aria-hidden="true">•</span>
                        <span>Full-quality uploads</span><span aria-hidden="true">•</span>
                        <span>12 months of hosting</span>
                    </div>
                </div>
            </section>

            {/* Why GatherLens */}
            <section className="py-20 bg-[#F8EFE6]">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-3xl mx-auto text-center mb-14">
                        <h2 className="text-3xl sm:text-4xl font-bold font-serif">Why Hosts Fall in Love with GatherLens</h2>
                        <p className="mt-4 text-base sm:text-lg text-gray-600">
                            We blend elegant technology with a human touch so you can savor every moment while knowing the memories are tenderly cared for.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {whyGatherLens.map((reason) => (
                            <div key={reason.title} className="rounded-2xl border border-[var(--champagne)] bg-[var(--off-white)]/80 p-8 text-center shadow-sm">
                                <div className="flex items-center justify-center">{reason.icon}</div>
                                <h3 className="mt-5 text-xl font-serif font-semibold text-[var(--charcoal)]">{reason.title}</h3>
                                <p className="mt-3 text-gray-600 leading-relaxed">{reason.copy}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Highlights */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-3xl mx-auto text-center mb-14">
                        <h2 className="text-3xl sm:text-4xl font-bold font-serif">Everything a Modern Host Needs</h2>
                        <p className="mt-4 text-base sm:text-lg text-gray-600">
                            From secure guest uploads to cinematic viewing, GatherLens keeps your celebration organized, on brand, and stress-free.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {highlightCards.map((card) => (
                            <div key={card.title} className="rounded-2xl border border-[var(--champagne)] bg-[var(--off-white)]/70 p-8 shadow-sm hover:shadow-lg transition-shadow duration-300">
                                <div className="flex items-center gap-4">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--blush)]">
                                        {card.icon}
                                    </div>
                                    <h3 className="text-2xl font-serif font-semibold text-[var(--charcoal)]">
                                        {card.title}
                                    </h3>
                                </div>
                                <p className="mt-4 text-gray-600 leading-relaxed">{card.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Guest Journey */}
            <section id="how-it-works" className="py-20 bg-[var(--off-white)]">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-3xl mx-auto text-center mb-14">
                        <h2 className="text-3xl sm:text-4xl font-bold font-serif">Guest-Friendly From First Snap to Final Toast</h2>
                        <p className="mt-4 text-base sm:text-lg text-gray-600">
                            The GatherLens flow is designed so every guest—from techy teens to grandparents—can contribute without needing help.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        {guestJourney.map((step, index) => (
                            <div key={step.title} className="relative rounded-2xl bg-white p-8 text-center shadow-md">
                                <span className="absolute -top-5 left-1/2 -translate-x-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--gold)] text-white text-lg font-semibold">
                                    {index + 1}
                                </span>
                                <div className="mt-6 flex items-center justify-center">
                                    {step.icon}
                                </div>
                                <h3 className="mt-5 text-xl font-serif font-semibold">{step.title}</h3>
                                <p className="mt-3 text-gray-600 text-sm sm:text-base leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Host Toolkit */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10 mb-14">
                        <div className="lg:max-w-xl">
                            <h2 className="text-3xl sm:text-4xl font-bold font-serif">Control Every Detail Without Compromise</h2>
                            <p className="mt-4 text-base sm:text-lg text-gray-600">
                                GatherLens is crafted for planners, couples, and coordinators who expect a premium experience. Customize the look, safeguard privacy, and collaborate with co-owners—always with the warmth of knowing you can lovingly curate or remove any moment.
                            </p>
                        </div>
                        <div className="flex flex-col gap-4 text-sm sm:text-base text-gray-600 lg:text-right">
                            <div className="inline-flex items-center gap-2 text-[var(--gold)] font-semibold">
                                <FaInfinity /> Unlimited uploads, guests, and albums per Event Pass
                            </div>
                            <div className="inline-flex items-center gap-2 text-[var(--gold)] font-semibold">
                                <MdOutlineCollectionsBookmark /> Live slideshow, private albums, and marketing-ready downloads
                            </div>
                            <div className="inline-flex items-center gap-2 text-[var(--gold)] font-semibold">
                                <FaCheckCircle /> 12-month upload & download window with effortless renewals
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {hostToolkit.map((block) => (
                            <div key={block.heading} className="rounded-2xl border border-[var(--champagne)] bg-[var(--off-white)] p-8 shadow-sm">
                                <div className="flex items-center gap-3 text-[var(--charcoal)]">
                                    {block.icon}
                                    <h3 className="text-xl font-serif font-semibold">{block.heading}</h3>
                                </div>
                                <ul className="mt-4 space-y-3 text-gray-600 text-sm sm:text-base leading-relaxed">
                                    {block.bullets.map((bullet) => (
                                        <li key={bullet} className="flex items-start gap-2">
                                            <span className="mt-1 h-2 w-2 rounded-full bg-[var(--gold)]"></span>
                                            <span>{bullet}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Event Experiences */}
            <section id="occasions" className="py-20 bg-[#F8EFE6]">
                <div className="container mx-auto px-4 sm:px-6 text-center max-w-4xl">
                    <p className="inline-flex items-center gap-3 rounded-full bg-white/70 px-5 py-2 text-sm uppercase tracking-[0.3em] text-[var(--charcoal)]">
                        For Every Event Worth Remembering
                    </p>
                    <h2 className="mt-6 text-3xl sm:text-4xl font-bold font-serif text-[var(--charcoal)]">
                        From weddings and reunions to product launches, milestone birthdays, funerals, and more, GatherLens keeps every guest contribution in one elegant archive—ready to download, share, or revisit long after the celebration.
                    </h2>
                    <p className="mt-6 text-base sm:text-lg text-gray-600">
                        Choose the moment you are planning and see how GatherLens supports the people who make it unforgettable.
                    </p>
                </div>
                <div className="container mx-auto px-4 sm:px-6 mt-16">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                        {eventExperienceSections.map((experience) => (
                            <button
                                key={experience.title}
                                type="button"
                                onClick={() => navigate(`events/${experience.slug}`)}
                                className="group flex flex-col items-center text-center gap-4 rounded-3xl border border-[var(--champagne)] bg-white/90 p-8 shadow-lg transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl"
                            >
                                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--blush)]/80">
                                    {experience.icon}
                                </span>
                                <h3 className="text-2xl font-serif font-semibold text-[var(--charcoal)]">
                                    {experience.title}
                                </h3>
                                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                                    {experience.summary}
                                </p>
                                <ul className="mt-2 space-y-3 text-left text-sm sm:text-base text-[var(--charcoal)]/80 leading-relaxed">
                                    {experience.details.map((detail) => (
                                        <li key={detail} className="flex items-start gap-2">
                                            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--gold)]"></span>
                                            <span>{detail}</span>
                                        </li>
                                    ))}
                                </ul>
                                <span className="mt-6 inline-flex items-center gap-2 text-[var(--gold)] font-semibold">
                                    {experience.cta}
                                    <span aria-hidden="true" className="transform transition-transform group-hover:translate-x-1">
                                        →
                                    </span>
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQs */}
            <section id="faq" className="py-20 bg-[var(--off-white)]">
                <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold font-serif">Quick Answers Before You Share the Link</h2>
                        <p className="mt-4 text-base sm:text-lg text-gray-600">
                            Still planning the rollout? These highlights cover what hosts ask first.
                        </p>
                    </div>
                    <div className="space-y-6">
                        {faqs.map((item) => (
                            <div key={item.q} className="rounded-2xl border border-[var(--champagne)] bg-[var(--off-white)] p-6 shadow-sm">
                                <h3 className="text-xl font-serif font-semibold text-[var(--charcoal)]">{item.q}</h3>
                                <p className="mt-3 text-gray-600 leading-relaxed">{item.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="py-20 bg-white">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-3xl mx-auto text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold font-serif">One Simple Price for the Whole Event</h2>
                        <p className="mt-4 text-base sm:text-lg text-gray-600">
                            Start in private preview mode today. Activate your Event Pass when you are ready for guests and enjoy everything—no surprise tiers or add-ons.
                        </p>
                    </div>
                    <div className="bg-white border-2 border-[var(--gold)] rounded-2xl shadow-2xl p-8 max-w-2xl mx-auto">
                        <h3 className="text-2xl sm:text-3xl font-serif font-bold">Event Pass</h3>
                        <p className="text-5xl font-bold my-6">
                            {hasDiscountedPrice && (
                                <span className="mr-2 text-2xl font-medium text-gray-400 line-through">{pricing.basePriceLabel}</span>
                            )}
                            {priceLabel} <span className="text-base font-normal text-gray-500">one-time</span>
                        </p>
                        {hasActiveOffer && (
                            <p className="-mt-4 mb-5 text-sm font-semibold text-[var(--gold-dark)]">
                                {pricing.offerLabel}: {offerPreview.savingsLabel || 'special pricing'}{pricing.codeRequired ? ` with code ${pricing.offerCode}` : ''}
                            </p>
                        )}
                        <ul className="text-left space-y-4 text-gray-600 text-sm sm:text-base leading-relaxed">
                            <li className="flex items-start gap-3">
                                <FaCheckCircle className="mt-1 text-[var(--gold)]" />
                                <span>Unlimited guests, albums, and full-quality uploads with 12 months of hosting.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <FaCheckCircle className="mt-1 text-[var(--gold)]" />
                                <span>Printable QR signage, private upload pages, and optional PIN.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <FaCheckCircle className="mt-1 text-[var(--gold)]" />
                                <span>Live slideshow, video guestbook, and message capture with instant host controls.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <FaCheckCircle className="mt-1 text-[var(--gold)]" />
                                <span>Download-all zip folders by album, co-owner access, and privacy controls you manage.</span>
                            </li>
                        </ul>
                        <button
                            onClick={onGetStarted}
                            className="mt-10 w-full btn-gold py-4 px-6 text-lg shadow-lg transform hover:scale-[1.02]"
                        >
                            Create Free, Share When Ready
                        </button>
                        <p className="mt-4 text-sm text-gray-500">
                            Need extra time? Renew for another year any time within 30 days of expiration.
                        </p>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 bg-[var(--off-white)]">
                <div className="container mx-auto px-4 sm:px-6 text-center max-w-3xl">
                    <h2 className="text-3xl sm:text-4xl font-bold font-serif">Ready to Host a Seamless Memory Hub?</h2>
                    <p className="mt-4 text-base sm:text-lg text-gray-600">
                        Create your gallery, invite co-owners, and prepare your slideshow in minutes. When the celebration begins, simply share the QR code and let guests do the rest.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
                        <button onClick={onGetStarted} className="btn-gold w-full sm:w-auto py-4 px-10 text-lg shadow-lg transform hover:scale-105">
                            Start in Preview Mode
                        </button>
                        <button
                            onClick={() => navigate('help')}
                            className="w-full sm:w-auto rounded-lg border border-[var(--gold)] py-4 px-8 text-lg font-semibold text-[var(--gold)] transition hover:bg-[var(--gold)] hover:text-white"
                        >
                            Browse Help Center
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
}

